import {
    type OverlayBackEntry,
    registerOverlay,
    releaseOverlay,
} from '@trustoo/hooks/overlayBackManager'
import { useCallback, useEffect, useRef } from 'react'

interface UseCloseOnBackProps {
    isOpen: boolean
    onClose: () => void
    overlayId: string
    /** Blocks `close()` while true. A Back press still closes. */
    preventClose?: boolean
}

/**
 * Lets the browser Back button close an open overlay.
 *
 * While the overlay is open it holds one extra history entry, added without changing the URL, so
 * pressing Back closes the overlay instead of leaving the page. The returned `close()` is for every
 * other way of closing (close button, backdrop, Escape, a resolve): it hands the entry back and
 * closes straight away, without going through history.
 *
 * Closing the overlay some other way — `hide()` on the underlying dialog, a parent flipping
 * `isOpen`, the component unmounting — releases the entry too, so a later Back press is never
 * silently swallowed.
 *
 * `preventClose` blocks every path that runs through `close()` — close button, backdrop, Escape —
 * for as long as the overlay must not be dismissed, typically an in-flight save. It has to sit here
 * rather than in the callers' `onClose`, because `close()` hands the history entry back before it
 * calls `onClose`: an `onClose` that declines leaves the overlay open with no entry behind it, and
 * the next Back press then spends itself on the stranded reservation and navigates away.
 *
 * A Back press still closes, whatever `preventClose` says. The browser has already left the entry
 * by the time the overlay is asked, so declining would mean pushing a replacement to stand in for
 * it and leaving the press with no effect at all.
 *
 * Which overlay a Back press closes is tracked in memory by `overlayBackManager`, one shared
 * bookkeeping per page behind a single popstate listener, so stacked overlays close strictly
 * top-first and routers that rewrite `window.history.state` (the Next.js App Router does) cannot
 * corrupt it.
 */
export const useCloseOnBack = ({
    isOpen,
    onClose,
    overlayId,
    preventClose = false,
}: UseCloseOnBackProps) => {
    const onCloseRef = useRef(onClose)
    const preventCloseRef = useRef(preventClose)
    const entryRef = useRef<OverlayBackEntry | null>(null)

    useEffect(() => {
        // Always use the latest close callback.
        onCloseRef.current = onClose
    }, [onClose])

    // Read through a ref so toggling it mid-flight keeps `close` referentially stable.
    useEffect(() => {
        preventCloseRef.current = preventClose
    }, [preventClose])

    useEffect(() => {
        if (!isOpen || typeof window === 'undefined') {
            return
        }

        const entry = registerOverlay(overlayId, () => onCloseRef.current())
        entryRef.current = entry

        return () => {
            entryRef.current = null
            releaseOverlay(entry)
        }
    }, [isOpen, overlayId])

    const close = useCallback(() => {
        if (preventCloseRef.current) {
            return
        }

        if (entryRef.current) {
            releaseOverlay(entryRef.current)
            entryRef.current = null
        }

        onCloseRef.current()
    }, [])

    return {
        close,
    }
}
